'use client';

import { c as _c } from "react/compiler-runtime";
import NextImage from 'next/image';
import { useMemo } from 'react';
import { type AppImageProps } from '@/app/components/media/app-image/app-image/types';
import { getImageFetchPriority } from '@/app/components/media/app-image/shared/image-load-state';
import { TRANSPARENT_IMAGE_PLACEHOLDER_SRC } from '@/app/components/media/app-image/shared/image-placeholder';
import { isSvgSrc, normalizeSrc } from '@/app/components/media/app-image/shared/image-source';
import { buildProjectImageUrl, getImageAttrs, getProjectImageKitUrlOptions, nextProjectImageLoader, sanitizeImageAlt } from '@/app/components/media/app-image/shared/project-adapter';
import { preconnectImageOrigin } from '@/app/components/media/app-image/shared/resource-hints';
import { useImageLoadState } from '@/app/components/media/app-image/shared/use-image-load-state';
import { ImageStyles, ImageWrapperStyles } from '@/app/components/media/app-image/styles';
import { cn } from '@/app/utils/cn';
const EMPTY_IMAGEKIT_PARAMS: NonNullable<AppImageProps['params']> = {};
const resolveAppImageSource = ({
  src,
  params
}: Pick<AppImageProps, 'src' | 'params'>) => {
  const normalizedSrc = normalizeSrc(src);
  const isSvg = isSvgSrc(normalizedSrc);
  const usesProjectImageLoader = typeof src === 'string' && !isSvg;
  const transformedSrc = usesProjectImageLoader ? buildProjectImageUrl(normalizedSrc, getProjectImageKitUrlOptions(params)) : src;
  return {
    imageKey: normalizeSrc(transformedSrc),
    isSvg,
    transformedSrc,
    usesProjectImageLoader
  };
};
const getWrapperStyleWithRatio = ({
  customRatio,
  height,
  style,
  width
}: {
  customRatio: boolean;
  height: AppImageProps['height'];
  style: AppImageProps['style'];
  width: AppImageProps['width'];
}) => {
  const hasNumericRatio = customRatio === true && typeof width === 'number' && typeof height === 'number' && width > 0 && height > 0;
  if (!hasNumericRatio) return style;
  return {
    ...style,
    aspectRatio: `${width} / ${height}`
  };
};
export const AppImage = t0 => {
  const $ = _c(47);
  const {
    src,
    alt,
    params: t1,
    fade: t2,
    hoverEffect,
    objectFit: t3,
    objectPosition: t4,
    placeholder: t5,
    preload,
    root,
    loading,
    preloadOffset: t6,
    className,
    imgClassName,
    style,
    imgStyle,
    imgRef,
    customRatio: t7,
    testId,
    fetchPriority,
    onLoad,
    ...props
  } = t0;
  const params = t1 === undefined ? EMPTY_IMAGEKIT_PARAMS : t1;
  const fade = t2 === undefined ? true : t2;
  const objectFit = t3 === undefined ? "cover" : t3;
  const objectPosition = t4 === undefined ? "center" : t4;
  const placeholder = t5 === undefined ? false : t5;
  const preloadOffset = t6 === undefined ? "0px 0px 0px 0px" : t6;
  const customRatio = t7 === undefined ? false : t7;
  let t8;
  if ($[0] !== params || $[1] !== src) {
    t8 = resolveAppImageSource({
      src,
      params
    });
    $[0] = params;
    $[1] = src;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  const resolvedSource = t8;
  if (preload) {
    preconnectImageOrigin(resolvedSource.transformedSrc);
  }
  const t9 = !!fade;
  const t10 = !!placeholder;
  let t11;
  if ($[3] !== loading || $[4] !== onLoad || $[5] !== preload || $[6] !== preloadOffset || $[7] !== resolvedSource.imageKey || $[8] !== root || $[9] !== t10 || $[10] !== t9) {
    t11 = {
      imageKey: resolvedSource.imageKey,
      fade: t9,
      placeholder: t10,
      preload,
      loading,
      root,
      preloadOffset,
      onLoad
    };
    $[3] = loading;
    $[4] = onLoad;
    $[5] = preload;
    $[6] = preloadOffset;
    $[7] = resolvedSource.imageKey;
    $[8] = root;
    $[9] = t10;
    $[10] = t9;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const {
    handleLoad,
    isLoaded,
    loadingAttr,
    shouldAnimatePlaceholder,
    shouldFade,
    shouldLoadImage,
    shouldPreloadImage,
    shouldShowPlaceholder,
    wrapperRef
  } = useImageLoadState(t11);
  const renderedImageSrc = shouldLoadImage ? resolvedSource.transformedSrc : TRANSPARENT_IMAGE_PLACEHOLDER_SRC;
  let t12;
  if ($[12] !== fetchPriority) {
    t12 = getImageFetchPriority({
      fetchPriority
    });
    $[12] = fetchPriority;
    $[13] = t12;
  } else {
    t12 = $[13];
  }
  const fetchPriorityAttr = t12;
  const wrapperStyleWithRatio = getWrapperStyleWithRatio({
    customRatio,
    height: props.height,
    style,
    width: props.width
  });
  const wrapperClassName = cn(ImageWrapperStyles({
    fill: props.fill,
    placeholder: shouldShowPlaceholder,
    animatePlaceholder: shouldAnimatePlaceholder,
    isLoaded: shouldShowPlaceholder && isLoaded
  }), className);
  let t13;
  if ($[14] !== hoverEffect || $[15] !== imgClassName || $[16] !== isLoaded || $[17] !== objectFit || $[18] !== objectPosition || $[19] !== shouldFade) {
    t13 = cn(ImageStyles({
      fade: shouldFade,
      hoverEffect,
      objectFit,
      objectPosition
    }), {
      "opacity-100": isLoaded && shouldFade,
      "is-loaded": isLoaded
    }, imgClassName);
    $[14] = hoverEffect;
    $[15] = imgClassName;
    $[16] = isLoaded;
    $[17] = objectFit;
    $[18] = objectPosition;
    $[19] = shouldFade;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  const imageClassName = t13;
  if (!src) {
    return null;
  }
  const t14 = testId ?? "app-image";
  let t15;
  if ($[21] !== t14) {
    t15 = getImageAttrs({
      testid: t14
    });
    $[21] = t14;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  const t16 = resolvedSource.imageKey;
  const t17 = shouldPreloadImage || undefined;
  let t18;
  if ($[23] !== alt) {
    t18 = sanitizeImageAlt(alt);
    $[23] = alt;
    $[24] = t18;
  } else {
    t18 = $[24];
  }
  const t19 = resolvedSource.usesProjectImageLoader ? nextProjectImageLoader : undefined;
  const t20 = resolvedSource.isSvg || renderedImageSrc === TRANSPARENT_IMAGE_PLACEHOLDER_SRC;
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = getImageAttrs({
      testid: "image"
    });
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] !== fetchPriorityAttr || $[27] !== handleLoad || $[28] !== imageClassName || $[29] !== imgRef || $[30] !== imgStyle || $[31] !== loadingAttr || $[32] !== props || $[33] !== renderedImageSrc || $[34] !== resolvedSource.imageKey || $[35] !== t17 || $[36] !== t18 || $[37] !== t19 || $[38] !== t20 || $[39] !== t21) {
    t22 = <NextImage key={t16} {...props} ref={imgRef} className={imageClassName} style={imgStyle} fetchPriority={fetchPriorityAttr} loading={loadingAttr} preload={t17} src={renderedImageSrc} alt={t18} loader={t19} unoptimized={t20} onLoad={handleLoad} {...t21} />;
    $[26] = fetchPriorityAttr;
    $[27] = handleLoad;
    $[28] = imageClassName;
    $[29] = imgRef;
    $[30] = imgStyle;
    $[31] = loadingAttr;
    $[32] = props;
    $[33] = renderedImageSrc;
    $[34] = resolvedSource.imageKey;
    $[35] = t17;
    $[36] = t18;
    $[37] = t19;
    $[38] = t20;
    $[39] = t21;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t15 || $[42] !== t22 || $[43] !== wrapperClassName || $[44] !== wrapperRef || $[45] !== wrapperStyleWithRatio) {
    t23 = <figure ref={wrapperRef} className={wrapperClassName} {...t15} style={wrapperStyleWithRatio}>{t22}</figure>;
    $[41] = t15;
    $[42] = t22;
    $[43] = wrapperClassName;
    $[44] = wrapperRef;
    $[45] = wrapperStyleWithRatio;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  return t23;
};